import { MessageBar, MessageBarBody } from '@fluentui/react-components';

import { EGroundedQaResultType, TGroundedQaRunResponse } from '../types';
import { TAiAssistantOrigin, type TAiAssistantSurface } from '../../types';
import { AI_NO_ANSWER_FOUND_TITLE, AI_OUT_OF_SCOPE_TITLE } from '../../constants';
import { useGroundedQaFeedback } from '../api';
import AiAssistantDisclaimer from '../../components/AiAssistantDisclaimer';
import AiAssistantAnswerCard from '../../components/AiAssistantAnswerCard';
import AiAssistantAnswerContent from '../../components/AiAssistantAnswerContent';
import AiAssistantSources from '../../components/AiAssistantSources';
import AiRunFeedback from '../../components/AiRunFeedback';
import useAiAssistantStore from '../../stores/useAiAssistantStore';
import useShouldAnimateAiAssistantRun from '../../hooks/useShouldAnimateAiAssistantRun.hook';

import ConfidenceBadge from './ConfidenceBadge';

type TProps = {
  response: TGroundedQaRunResponse;
  surface: TAiAssistantSurface;
  origin: TAiAssistantOrigin | undefined;
};

const GroundedQaAnswerView = ({ response, surface, origin }: TProps) => {
  const animated = useShouldAnimateAiAssistantRun(response.runId, surface, origin);
  const feedbackMutation = useGroundedQaFeedback();
  const { read, update } = useAiAssistantStore();
  const isAnswer = response.resultType === EGroundedQaResultType.ANSWER;
  const isOutOfScope = response.resultType === EGroundedQaResultType.OUT_OF_SCOPE;

  if (!response.resultType || !response.result) return null;

  const { answer, citationConfidence, citationSources } = response.result;

  return (
    <div className="flex flex-col h-full min-h-0">
      {!isAnswer && (
        <div className="mx-3 mt-3 shrink-0">
          <MessageBar intent="warning" layout="multiline">
            <MessageBarBody>{isOutOfScope ? AI_OUT_OF_SCOPE_TITLE : AI_NO_ANSWER_FOUND_TITLE}</MessageBarBody>
          </MessageBar>
        </div>
      )}
      <AiAssistantAnswerCard
        style={{ flex: '3 1 0%' }}
        footer={
          <AiRunFeedback
            key={response.runId}
            runId={response.runId}
            feedbackMutation={feedbackMutation}
            submittedFeedback={read(surface, origin).submittedFeedback}
            onFeedbackSubmitted={(feedback) => update(surface, origin, { submittedFeedback: feedback })}
          />
        }
      >
        <AiAssistantAnswerContent answer={answer} animated={animated} />
      </AiAssistantAnswerCard>
      <div className="px-4 pt-2 shrink-0">
        <AiAssistantDisclaimer />
      </div>
      {!isOutOfScope && (!isAnswer || citationSources.length > 0) && (
        <div className="min-h-0 mx-3" style={{ flex: '2 1 0%' }}>
          <AiAssistantSources
            citationSources={citationSources}
            item={origin?.item}
            runId={response.runId}
            headerAccessory={isAnswer ? <ConfidenceBadge confidence={citationConfidence} /> : undefined}
          />
        </div>
      )}
    </div>
  );
};

export default GroundedQaAnswerView;
